import { getEasternDate, CSVToArray } from '~/utils';
import { compareDates, formatDate, getCutoffTime, getWeekdayName, parseProductPreorderDates } from '~/utils/calendar-utils';
import { isDeliverySurcharge, isFeeDate } from '~/utils/fee-utils-2';
import { DateFormatters } from '~/utils/date-formatting';
import { isShipTestBVariant } from '~/utils/shiptest-3';
/**
 * DeliveryDateService is a class that handles the delivery date logic
 * @property {Date} deliveryDate - the delivery date
 * @property {Date} deliveryDateISO - the delivery date in ISO format
 * @property {Date} shippingDate - the shipping date
 * @method isDateAvailable - checks if a date is available
 */
export class DeliveryDateService {
  #globalBlockOutDates;
  #productBlockOutDates;
  #repeatingDisabledDates;
  #overrideBlockedDates;
  #productSoldOutDates;
  #globalSoldOutDates;
  #cutoffTime;
  #premiumDeliveryDates;
  #premiumDeliveryDaysOfWeek;
  #productPreorderRange;

  constructor(config, productBlockOutDates, productSoldOutDates, productPreorderDates) {
    // All dates are in the format 'MM-DD-YYYY'
    // globalBlockOutDates is an array of dates (e.g. ['01-01-2022', '01-02-2022'])
    this.#globalBlockOutDates = CSVToArray(config.blockoutDates.global);
    // productBlockOutDates is an array of dates (e.g. ['01-01-2022', '01-02-2022'])
    this.#productBlockOutDates = CSVToArray(config.blockoutDates.product.blockout);
    // productSoldOutDates is  an array of dates (e.g. ['01-01-2022', '01-02-2022'])
    this.#productSoldOutDates = CSVToArray(config.blockoutDates.product.soldout);
    // repeatingDisabledDates is an array of weekdays (e.g. ['Monday', 'Tuesday'])
    this.#repeatingDisabledDates = CSVToArray(config.blockoutDates.repeatingDisabledDates);
    // cutoffTime is the time of the day when the cutoff is applied
    this.#cutoffTime = getCutoffTime();
    // overrideBlockedDates is an array of dates (e.g. ['01-01-2022', '01-02-2022'])
    this.#overrideBlockedDates = CSVToArray(config.overrideBlockoutDates);
    // globalSoldOutDates
    this.#globalSoldOutDates = CSVToArray(config.blockoutDates.globalSoldoutDates);

    // if productBlockOutDates is provided, use it instead of the globally defined product block out dates
    if (productBlockOutDates) {
      this.#productBlockOutDates = productBlockOutDates;
    }

    // if productSoldOutDates is provided, use it instead of the globally defined product block out dates
    if (productSoldOutDates) {
      this.#productSoldOutDates = productSoldOutDates
    }

    // Product-level preorder range
    this.#productPreorderRange = parseProductPreorderDates(productPreorderDates);

    // Public properties
    this.deliveryDate = this.#getDeliveryDate();
    this.deliveryDateISO = this.deliveryDate.toISOString();
    this.shippingDate = this.#getShippingDate();
    this.deliverySurcharge = config.deliverySurcharge;
    this.fees = config.fees || [];
    this.feePrecedence = config.feePrecedence;
    this.premiumDelivery = config.premiumDelivery;
    this.freeDayColor = config.freeDayColor;
    this.privatePreorder = config.privatePreorder;
    this.generalPreorder = config.generalPreorder;
    this.cutoffTime = this.#cutoffTime;

    // premiumDeliveryDaysOfWeek lists weekday names that are always premium for dessert
    // boards (e.g. ['Tuesday']). Sourced from the dashboard's "Premium Delivery Days"
    // setting via boarderie_dessert_boards.premium_delivery_days. Falls back to
    // ['Tuesday'] when the metafield isn't yet configured (carrier reality: Tuesday
    // delivery requires Monday overnight ship — no Sunday carrier pickup).
    // An explicit empty string from the dashboard means "no day-of-week premium".
    this.#premiumDeliveryDaysOfWeek = this.#parsePremiumDeliveryDaysOfWeek(config.premiumDelivery?.days);

    // premiumDeliveryDates is an array of dates (e.g. ['01-01-2022', '01-02-2022'])
    this.#premiumDeliveryDates = this.#generatePremiumDeliveryDates();
    // nonPremiumDeliveryDate is the next available non premium delivery date
    this.nonPremiumDeliveryDate = this.#getNonPremiumDeliveryDate();
    // nonPremiumDeliveryDateISO is the next available non premium delivery date in ISO format
    this.nonPremiumDeliveryDateISO = this.nonPremiumDeliveryDate.toISOString();
    // nonPremiumShippingDate is the shipping date for the next available non premium shipping date (2 days before)
    this.nonPremiumShippingDate = this.#getNonPremiumShippingDate();
    // premiumShippingDate is the shipping date for premium/overnight delivery (1 day before delivery)
    this.premiumShippingDate = this.#getPremiumShippingDate();
  }

  get productPreorderRange() {
    return this.#productPreorderRange;
  }

  /**
   * Get the formatted delivery date
   * @param {string} format
   * @param {boolean} skipPremiumDay
   * @returns {string} formatted delivery date
   */
  getFormattedDeliveryDate(format, skipPremiumDay = false) {
    return this.#getFormattedDeliveryDate(format, skipPremiumDay);
  }

  /**
   * Check if a date is blocked
   * @param {Date} date
   * @returns {{result: boolean, conditions: string[]}}
   *   Returns an object with the following properties:
   *   - result: true if the date is blocked, false otherwise
   *   - conditions: an array of conditions that caused the date to be blocked
   */
  isBlockedDate(date) {
    const formattedDate = formatDate({ date });
    const { date: easternTodaysDate, hour: easternCurrentHour } = getEasternDate();
    const easternCurrentDay = easternTodaysDate.getDay();

    // Get the date for the next day
    const { date: easternNextDate } = getEasternDate();
    easternNextDate.setDate(easternNextDate.getDate() + 1);

    // Cutoff Validations -------------------->
    let monthChanged = false;
    if (easternNextDate.getMonth() !== easternTodaysDate.getMonth()) {
      monthChanged = true;
    }

    let yearChanged = false;
    if (easternNextDate.getFullYear() !== easternTodaysDate.getFullYear()) {
      yearChanged = true;
    }

    const passedCutoffTime = easternCurrentHour >= this.#cutoffTime;
    const validateCutoffTimeDate = date.getDate() === easternNextDate.getDate();

    let validateCutoffTimeMonth = date.getMonth() === easternTodaysDate.getMonth();
    if (monthChanged) {
      validateCutoffTimeMonth = date.getMonth() === easternNextDate.getMonth();
    }

    let validateCutoffTimeYear = date.getFullYear() === easternTodaysDate.getFullYear();
    if (yearChanged) {
      validateCutoffTimeYear = date.getFullYear() === easternNextDate.getFullYear();
    }

    const isAfterCuttoffTime = passedCutoffTime && validateCutoffTimeDate && validateCutoffTimeMonth && validateCutoffTimeYear
    // <--------------------- Cutoff Validations

    const isBeforeToday = date <= easternTodaysDate;

    const isSaturdaySpecialCase = [
      getWeekdayName(date) === 'Monday', // if the date is monday
      compareDates(date, new Date(this.deliveryDateISO)), // is date is next available day,
      easternCurrentDay === 6, // if today is saturday
      date.getDate() - 2 === easternTodaysDate.getDate(), // if saturday is two days before the date(monday)
      easternCurrentHour > this.#cutoffTime, // if current time is after saturday's cuttoff time
    ].every(Boolean)

    // if today is sunday, we need to block monday.
    const isSundaySpecialCase = [
      getWeekdayName(date) === 'Monday', // if the date is monday
      compareDates(date, new Date(this.deliveryDateISO)), // is date is next available day,
      easternCurrentDay === 0, // if today is sunday
      date.getDate() - 1 === easternTodaysDate.getDate(), // if sunday is one day before the date(monday)
    ].every(Boolean)

    const isBlockedChecks = [
      { condition: 'global', result: this.#globalBlockOutDates.includes(formattedDate) },
      { condition: 'product-blockout', result: this.#productBlockOutDates.includes(formattedDate) },
      { condition: 'product-soldout', result: this.#productSoldOutDates.includes(formattedDate) || this.#globalSoldOutDates.includes(formattedDate) },
      { condition: 'repeating', result: this.#repeatingDisabledDates.includes(getWeekdayName(date)) },
      { condition: 'beforeToday', result: isBeforeToday },
      {
        condition: 'afterCutoff',
        result: isAfterCuttoffTime,
      },
      {
        condition: "saturdaySpecialCase",
        result: isSaturdaySpecialCase,
      },
      {
        condition: "sundaySpecialCase",
        result: isSundaySpecialCase,
      },
      {
        condition: 'outside-product-preorder',
        result: this.#productPreorderRange
          ? (date < this.#productPreorderRange.startDate || date > this.#productPreorderRange.endDate)
          : false,
      },
    ];

    const isOutsideProductPreorder = this.#productPreorderRange
      ? (date < this.#productPreorderRange.startDate || date > this.#productPreorderRange.endDate)
      : false;

    // Check if the date is always enabled
    if (
      this.#overrideBlockedDates.length > 0 &&
      this.#overrideBlockedDates.includes(formattedDate) &&
      !isAfterCuttoffTime &&
      !isBeforeToday &&
      !this.#productBlockOutDates.includes(formattedDate) &&
      !this.#productSoldOutDates.includes(formattedDate) &&
      !isSaturdaySpecialCase &&
      !isSundaySpecialCase &&
      !isOutsideProductPreorder
    ) {
      return {
        result: false,
        conditions: [],
      };
    }

    const blockedConditions = isBlockedChecks.filter((check) => check.result).map((check) => check.condition);

    return {
      result: blockedConditions.length > 0,
      conditions: blockedConditions,
    };
  }

  /**
   * Checks if a date has any applicable fees.
   * @param {Date} date - The date to check.
   * @param {string} boardType - The board type ('cheese-board' or 'dessert-board')
   * @returns {Array<{type?: string, id?: string, title?: string, cost: number, color: string}>}
   *   Returns an array of matching fees. Each fee object may include:
   *   - type: 'deliverySurcharge' or 'premiumDelivery' (for special fee types)
   *   - id: Fee identifier (for date-based fees)
   *   - title: Fee title (for date-based fees)
   *   - cost: The fee cost in dollars
   *   - color: The color associated with the fee
   */
  getFees(date, boardType = 'cheese-board') {
    const matchingFees = [];

    // Check delivery surcharge (day-based)
    if (this.deliverySurcharge?.days && this.deliverySurcharge?.cost) {
      const daysArray = this.deliverySurcharge.days.split(',').map((d) => d.trim());
      if (isDeliverySurcharge(date, daysArray)) {
        matchingFees.push({
          type: 'deliverySurcharge',
          cost: this.deliverySurcharge.cost,
          color: this.deliverySurcharge.color,
        });
      }
    }

    // Check date-based fees from fees array
    this.fees.forEach((fee) => {
      if (!fee.cost || !fee.dates) return;

      const datesArray = fee.dates.split(',').map((d) => d.trim());
      if (isFeeDate(date, datesArray)) {
        matchingFees.push({
          id: fee.id,
          title: fee.title,
          cost: fee.cost,
          color: fee.color,
        });
      }
    });

    // If multiple date-based fees match, filter by precedence
    const dateFees = matchingFees.filter((fee) => fee.id);
    if (dateFees.length > 1 && this.feePrecedence) {
      const precedentFee = dateFees.find((fee) => fee.id === this.feePrecedence);
      if (precedentFee) {
        const nonDateFees = matchingFees.filter((fee) => !fee.id);
        matchingFees.length = 0;
        matchingFees.push(...nonDateFees, precedentFee);
      }
    }

    // Check premium delivery (dessert boards only).
    // Skipped on the Shipping Test B Variant theme — that test removes the dessert-board surcharge.
    if (boardType === 'dessert-board' && this.premiumDelivery?.cost && !isShipTestBVariant()) {
      if (this.isPremiumDeliveryDay(date)) {
        matchingFees.push({
          type: 'premiumDelivery',
          cost: this.premiumDelivery.cost,
          color: this.premiumDelivery.color,
        });
      }
    }

    return matchingFees;
  }

  /**
   * Get the next available non premium delivery date
   * @returns {Date} next available non premium delivery date
   */
  #getNonPremiumDeliveryDate() {
    let iterations = 0;
    let nonPremiumDeliveryDate = this.deliveryDate;
    const maxIterations = 30;

    while (iterations < maxIterations) {
      const deliveryDate = new Date(nonPremiumDeliveryDate);
      deliveryDate.setDate(deliveryDate.getDate() + iterations);

      if (this.isPremiumDeliveryDay(deliveryDate) || this.isBlockedDate(deliveryDate).result) {
        iterations++;
      } else {
        nonPremiumDeliveryDate = deliveryDate;
        break;
      }
    }

    return nonPremiumDeliveryDate;
  }

  #getNonPremiumShippingDate() {
    const nonPremiumDeliveryDate = this.#getNonPremiumDeliveryDate();
    const shippingDate = new Date(nonPremiumDeliveryDate);
    // By definition #getNonPremiumDeliveryDate skips premium days (Tuesday + any
    // merchant-configured days), so the date here always uses standard 2-day shipping.
    shippingDate.setDate(nonPremiumDeliveryDate.getDate() - 2);
    return shippingDate;
  }

  /**
   * Get the premium shipping date for dessert boards with overnight delivery
   * Premium shipping is 1 day before delivery (overnight)
   * @returns {Date} premium shipping date
   */
  #getPremiumShippingDate() {
    const shippingDate = new Date(this.deliveryDate);
    shippingDate.setDate(this.deliveryDate.getDate() - 1);
    return shippingDate;
  }

  #generatePremiumDeliveryDates() {
    const premiumDeliveryDates = [];
    const premiumDeliveryDaysBeforeFreeDelivery = this.premiumDelivery?.daysBeforeFreeDelivery ?? 0;

    if (premiumDeliveryDaysBeforeFreeDelivery === 0) {
      return premiumDeliveryDates;
    }

    // If blockout dates pushed the delivery date beyond the expected date,
    // skip the standard N-day premium window — it's not truly overnight/rush shipping.
    const { date: todayEastern, hour: currentHour } = getEasternDate();
    const todayDayOfWeek = todayEastern.getDay(); // 0=Sun, 6=Sat
    const expectedDate = new Date(todayEastern);

    if (todayDayOfWeek === 0 || todayDayOfWeek === 6) {
      // Saturday or Sunday: expected premium date is next Tuesday
      const daysUntilTuesday = todayDayOfWeek === 6 ? 3 : 2;
      expectedDate.setDate(expectedDate.getDate() + daysUntilTuesday);
    } else {
      // Weekday: before cutoff → tomorrow, after cutoff → day after tomorrow
      const isBeforeCutoff = currentHour < this.#cutoffTime;
      expectedDate.setDate(expectedDate.getDate() + (isBeforeCutoff ? 1 : 2));
    }

    if (!compareDates(expectedDate, this.deliveryDate)) {
      return premiumDeliveryDates;
    }

    let iterations = 0;
    const maxIterations = 30;

    while (iterations < maxIterations && premiumDeliveryDates.length < premiumDeliveryDaysBeforeFreeDelivery) {
      const { date: easternDate } = getEasternDate();
      const nextEasternDate = new Date(easternDate);
      nextEasternDate.setDate(nextEasternDate.getDate() + iterations);

      if (!this.isBlockedDate(nextEasternDate).result) {
        const formattedDate = formatDate({ date: nextEasternDate });
        if (!premiumDeliveryDates.includes(formattedDate)) {
          premiumDeliveryDates.push(formattedDate);
        }
      }

      iterations++;
    }

    return premiumDeliveryDates;
  }

  /**
   * Check if a date is a premium delivery day. Premium days fall into three buckets:
   *  1. Tuesday — unconditionally premium. Tuesday delivery requires Monday overnight
   *     shipping because carriers don't pick up on Sundays. This is a logistics fact,
   *     not a merchant pricing dial, so it is hardcoded.
   *  2. Additional day-of-week premium (merchant-configured via the dashboard
   *     "Additional Premium Delivery Days" setting).
   *  3. Date-list premium (the standard N-day window from #generatePremiumDeliveryDates).
   * Every caller is already gated on `boardType === 'dessert-board'`.
   * @param {Date} date
   * @returns {boolean} true if the date is a premium delivery day, false otherwise
   */
  isPremiumDeliveryDay(date) {
    if (date?.getDay() === 2) return true;
    if (this.#isPremiumDayOfWeek(date)) return true;
    return this.#premiumDeliveryDates.includes(formatDate({ date }));
  }

  /**
   * True if the given date falls on a configured premium day-of-week.
   * @param {Date} date
   * @returns {boolean}
   */
  #isPremiumDayOfWeek(date) {
    if (!date || !this.#premiumDeliveryDaysOfWeek?.length) return false;
    return this.#premiumDeliveryDaysOfWeek.includes(getWeekdayName(date));
  }

  /**
   * Parse premium-delivery day-of-week config from the metafield bridge.
   * - undefined / null  → backward-compat default of ['Tuesday']
   * - "" (empty string) → explicit "no day-of-week premium" from the dashboard
   * - "Tuesday,Friday"  → ['Tuesday', 'Friday']
   * @param {string|null|undefined} daysCsv
   * @returns {string[]}
   */
  #parsePremiumDeliveryDaysOfWeek(daysCsv) {
    if (daysCsv === undefined || daysCsv === null) return ['Tuesday'];
    if (typeof daysCsv !== 'string') return [];
    return daysCsv.split(',').map((d) => d.trim()).filter(Boolean);
  }

  /**
   * Get the formatted delivery date
   * @param {string} format
   * @param {boolean} skipPremiumDay
   * @returns {string} formatted delivery date
   */
  #getFormattedDeliveryDate(format, skipPremiumDay = false) {
    const immediateDate = this.deliveryDate;
    const nonPremiumDate = this.#getNonPremiumDeliveryDate();

    return DateFormatters.formatForDisplay({
      date: skipPremiumDay ? nonPremiumDate : immediateDate,
      format,
      referenceDate: skipPremiumDay ? nonPremiumDate : immediateDate
    });
  }

  /**
   * Get the delivery date
   * @returns {Date} delivery date
   */
  #getDeliveryDate() {
    const cutoffTime = this.#cutoffTime;

    // Create a new Date object set to Eastern Time
    let { date: easternDate, hour } = getEasternDate();
    let deliveryDate = easternDate;

    // Determine if the order was placed before or after the cutoff time
    const isBeforeCutOffTime = hour < cutoffTime;
    if (isBeforeCutOffTime) {
      // Order placed before cutoff time
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    } else {
      // Order placed after cutoff time
      deliveryDate.setDate(deliveryDate.getDate() + 2);
    }

    // Find the next available date considering block out dates
    return this.#getNextAvailableDeliveryDate(deliveryDate);
  }

  /**
   * Get the next available delivery date
   * @param {Date} deliveryDate
   * @returns {Date} next available delivery date
   */
  #getNextAvailableDeliveryDate(deliveryDate) {
    const maxIterations = 30;
    let iterations = 0;

    while (iterations <= maxIterations) {
      const { result: isBlocked, conditions } = this.isBlockedDate(deliveryDate);

      if (!isBlocked) {
        return deliveryDate;
      } else {
        console.debug(`Date ${deliveryDate} is blocked due to: ${conditions.join(', ')}`);
      }

      deliveryDate.setDate(deliveryDate.getDate() + 1);

      iterations++;
    }

    console.error('Could not find an available delivery date within 30 days.');
    return null;
  }

  /**
   * Get the shipping date
   * Shipping date is the delivery date minus 1 day
   * @returns {Date} shipping date
   */
  #getShippingDate() {
    const shippingDate = new Date(this.deliveryDate);
    shippingDate.setDate(this.deliveryDate.getDate() - 1);
    return shippingDate;
  }
}